<template>
    <!-- Slide-Over to contain Multi-Point Calibration Profile creation -->
    <div    v-if="canArchiveCalibrationProfiles">
        <slide-over-panel-wide      v-model:show="showCreateMultiPointCalibrationProfile"
        >
            <template   #title>Create Multi-Point Calibration Profile</template>
            <template   #content>
                <!-- Component to create profile -->
                <multi-point-calibration-profile-create     :probe-types="probeTypes"
                                                            @profile-created="profileCreated"
                ></multi-point-calibration-profile-create>
            </template>
        </slide-over-panel-wide>
    </div>

    <div class="flex justify-center w-full">
        <div class="flex flex-col w-full items-center">
            <!-- Contents -->
            <h2>Multi-Point Calibration Profile</h2>

            <!-- Table for Multi-calibration index -->
            <div    class="w-5/6 2xl:w-2/3">
                <ajax-table     v-model:items-per-page="ajaxTable.itemsPerPage"
                                v-model:loading="ajaxTable.loading"
                                v-model:page="ajaxTable.page"
                                v-model:page-info="ajaxTable.pageInfo"
                                v-model:search="ajaxTable.search"
                                v-model:sort-by="ajaxTable.sortBy"
                                v-model:sort-desc="ajaxTable.sortDesc"
                                :data="profiles"
                                :exportable="false"
                                :headers="headers"
                                :row-selectable="false"
                                no-data-message="No Profiles Found."
                                @loadData="getData"
                                @update:search="updateSearchUrl"

                >
                    <!-- Create Button to the right of search -->
                    <template   #search-right>
                        <button-green-primary    @click="showCreateMultiPointCalibrationProfile = true">
                            Create Multi-Point Calibration Profile
                        </button-green-primary>
                    </template>

                    <!-- Column of table for disable action dropdown -->
                    <template   #col-5="props">
                        <div    class="w-full flex justify-end overflow-visible"
                                v-if="canArchiveCalibrationProfiles"
                                @click.stop>
                            <!-- Headless UI -->
                            <Menu as="div" class="inline-block text-left w-2/3">
                                <div>
                                    <MenuButton class="text-left w-2/3 inline-flex w-full justify-between rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                                        <span>Action</span>
                                        <div    class="h-5 w-5 mx-2">
                                            <span   :class="iconSortDesc"></span>
                                        </div>
                                    </MenuButton>
                                </div>

                                <MenuItems class="absolute mt-2 w-56 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                           style="">
                                    <div class="py-1">
                                        <MenuItem v-slot="{ active }">
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm cursor-pointer']"
                                                @click="retireMultiPointCalibrationProfile(props.rowIndex)">
                                            <span    class="h-6 w-6 mr-3">
                                                <span   class="lnr icon-drawers text-gray-400"></span>
                                            </span>
                                            Archive Multi-Point Calibration Profile
                                        </span>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </Menu>
                        </div>
                    </template>

                </ajax-table>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";

import AjaxTable from "../elements/AjaxTable.vue";
import _ from "lodash";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/vue";
import {UrlParamsMixin} from "../../mixins/UrlParamsMixin";
import SlideOverPanelWide from "../elements/SlideOverPanelWide.vue";
import MultiPointCalibrationProfileCreate from "./MultiPointCalibrationProfileCreate.vue";
import ButtonGreenPrimary from "../elements/buttons/ButtonGreenPrimary.vue";

export default {
    name: "MultiPointCalibrationProfileIndex",
    mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin, UrlParamsMixin],
    components: {
        ButtonGreenPrimary,
        SlideOverPanelWide, Menu, MenuButton, MenuItems, MenuItem, AjaxTable, MultiPointCalibrationProfileCreate},
    emits: [],
    props: {
        canArchiveCalibrationProfiles: {
            type: Boolean,
            default: false,
        },
        // Props
    },
    data() {
        return {
            // Data Properties
            headers: [
                {
                    name: 'Profile Name',
                    key: 'name',
                },
                {
                    name: 'Probe Type',
                    key: 'probe_type',
                },
                {
                    name: 'Reference Document',
                    key: 'reference_document_name',
                },
                {
                    name: 'Thresholds',
                    key: 'thresholds',
                },
                {
                    name: '',
                    key: '',
                },
            ],
            profiles: [],
            requestNumber: 0,
            probeTypes: [],

            // Create Props
            showCreateMultiPointCalibrationProfile: false,
        }
    },
    created() {
        this.ajaxTable.sortBy = 'created_at'
        this.ajaxTable.sortDesc = true
    },
    mounted() {
        this.ajaxTable.search = this.urlParamsGet('search', '')
    },
    methods: {
        getData() {
            // Get data from API
            this.ajaxTable.loading = true
            this.requestNumber++
            let params = JSON.parse(JSON.stringify(this.ajaxTable))
            params.pageInfo = null
            params.requestNumber = this.requestNumber

            axios.get(this.company.buildApiInternalRoute('multi-point-calibration-profiles'),
                {
                    params: params,
                }
            ).then(response => {
                // Grab Data and if meta doesn't exist or request number doesn't match, quit out.
                let data = response.data
                if (_.isNil(data.meta) || data.meta.requestNumber !== `${this.requestNumber}`) {
                    return
                }

                // Update Table Data to AjaxTable
                this.ajaxUpdateTableData(data)
                this.profiles = ! _.isNil(data.data) ? data.data : []
                this.probeTypes = data.meta.probeTypes
            }).catch(error => {
                console.log(error)
            }).then(() => {
                this.ajaxTable.loading = false
            })

        },
        profileCreated() {
            // Close Slide-Over and refresh data
            this.showCreateMultiPointCalibrationProfile = false
            this.getData()
        },
        retireMultiPointCalibrationProfile(index) {
            // Retire Multi Point Calibration Profile
            let profileId = this.profiles[index].id
            axios.delete(this.company.buildApiInternalRoute('multi-point-calibration-profiles/' + profileId),
            ).then(response => {
                this.getData()
            }).catch(error => {
                console.log(error)
            })
        },
        updateSearchUrl(search) {
            // Update Search URL
            if (_.isNil(search) || search === '') {
                this.urlParamsRemove('search')
            }
            this.urlParamsStore('search', search)
        },
    },
    computed: {},
    watch: {

    },
}
</script>

<style scoped>

</style>